<template>
  <div class="image-gallery" :class="{ 'is-square-aspect-ratio': isSquareAspectRatio }">
    <template v-if="isMobile">
      <MobileCarousel
        ref="mobileCarousel"
        :list="filteredImages"
        data-test-id="image-gallery-mobile"
        @first-slide-toggle="isFirstSlideActive = $event"
        @slideshow-change="handleCarouselChange"
      >
        <template #overlay>
          <slot v-if="$slots['product-short-summary']" name="product-short-summary" :is-first-slide-active="isFirstSlideActive" :index="0" />
        </template>
        <template #item="{ data, index }">
          <div v-if="data.video" class="product-video-wrapper">
            <VideoPlayer :video-id="videoId" load-video-player-on-mount />
          </div>
          <div v-else class="product-image-wrapper">
            <PictureView
              :image-url="data.path"
              fetch-priority
              :lazy-load="index > 0"
              :alt="productImgAlt"
              :aspect-ratio="isSquareAspectRatio ? 1 : 0.8"
              :image-sizes-by-breakpoints="imageSizesByBreakpoints"
              :ai-image="isAiGeneratedMetaData"
              :ai-image-overrides="aiImageOverride"
              @click.prevent.stop="openImageOverlay(index)"
            />
          </div>
        </template>
      </MobileCarousel>
    </template>

    <Carousel
      v-else
      :list="filteredImages"
      :number-of-items="{ large: 3, medium: 3, small: 3 }"
      :gutter="{ large: 8, medium: 8, small: 8 }"
      :ssr-size="currentSsrSize"
      data-test-id="image-gallery-desktop"
      @carousel-next="handleCarouselNext"
    >
      <template #item="{ data, index }">
        <div v-if="data.video" class="product-video-wrapper">
          <VideoPlayer :video-id="videoId" load-video-player-on-mount />
        </div>
        <div v-else class="product-image-wrapper">
          <slot v-if="$slots['product-short-summary']" name="product-short-summary" :is-first-slide-active="true" :index="index" />
          <PictureView
            :image-url="data.path"
            :alt="productImgAlt"
            fetch-priority
            :lazy-load="index > 2"
            :aspect-ratio="isSquareAspectRatio ? 1 : 0.8"
            :image-sizes-by-breakpoints="imageSizesByBreakpoints"
            :ai-image="isAiGeneratedMetaData"
            :ai-image-overrides="aiImageOverride"
            @click.prevent.stop="openImageOverlay(index)"
          />
        </div>
      </template>
    </Carousel>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, unref, toValue } from 'vue';

import useImages from '~/composables/images/useImages';
import { useImageryStateProvide, useImageryState } from '~/composables/useImageryState';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useBrand } from '~/composables/useBrand';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useModal } from '~/composables/utils/useModal';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useDivisionCategoryFlags } from '~/modules/featureControl/composables/useDivisionCategoryFlags';
import { tagSTCAltImage, tagSTCAltLastImage } from '~/features/analytics/shopTheCollection';

import Carousel from '@atomic-ui/carousel/Carousel.vue';
import MobileCarousel from '~/components/common/Media/SlideShowImproved.vue';
import PictureView from '~/components/common/Media/PictureView.vue';
import VideoPlayer from '~/components/NewProductVideo.vue';
import { AI_WATERMARK_CONFIG } from '~/constants/common';

const props = withDefaults(
  defineProps<{
    disableOverlay?: boolean;
  }>(),
  {
    disableOverlay: false,
  },
);

useImageryStateProvide();

const isFirstSlideActive = ref(true); // only MEW
const { isMobile, isDesktop } = useDeviceType();
const { carouselImages, videoId, pauseVideo } = useImages();
const { isBcom } = useBrand();
const { productName, topLevelCategoryName } = useCommonProductGetters();
const { updateOverlayOpen, updateCarousel, isOverlayOpen } = useImageryState();
const { open } = useModal();
const { isHomeCategory } = useTopLevelCategoryFlags();
const { beautyFlag, homeFOBFlag, isAiGeneratedMetaData } = useProductFlags();
const { isFineJewelryCategory, isToysCategory } = useDivisionCategoryFlags();

const isSquareAspectRatio = computed(() => {
  const isSquareCategory = ['Home', 'Beauty'].includes(unref(topLevelCategoryName));
  const hasSquareFlag = isHomeCategory || homeFOBFlag || beautyFlag || isFineJewelryCategory || isToysCategory;

  return hasSquareFlag || isSquareCategory;
});

const productImgAlt = computed(() => {
  const name = toValue(productName);
  return isAiGeneratedMetaData ? `${name} Note: This image is AI-generated.` : `${name} image`;
});

const filteredImages = computed(() => carouselImages.value.filter((image) => !image?.imageIndicatorEnabled));
const currentSsrSize = computed(() => (isDesktop.value ? 'large' : 'medium'));
const imageSizesByBreakpoints = {
  small: 500,
  medium: 700,
  large: 700,
  mlarge: 700,
  xlarge: 700,
};

/**
 * Maps the local gallery index (from filteredImages) to the global carousel index (including AR indicators).
 * Necessary because the global imagery state (Pinia) expects the absolute position of the item,
 * while this component only renders a subset of items.
 */
const getFullIndex = (filteredIndex: number): number => {
  let count = 0;
  for (let i = 0; i < carouselImages.value.length; i++) {
    const img = carouselImages.value[i];
    // Skip items that are filtered out in this component (e.g., AR indicators)
    if (!img?.imageIndicatorEnabled) {
      count++;
    }
    // Once the count matches our local filtered index, return the original absolute index (1-based)
    if (count === filteredIndex) {
      return i + 1;
    }
  }
  return 1;
};

const openImageOverlay = (index?: number) => {
  if (props.disableOverlay) return;

  if (typeof index === 'number') {
    updateCarousel(getFullIndex(index + 1));
  }
  pauseVideo();
  updateOverlayOpen(true);
  open('main-image-overlay');
};

const trackAltImageAnalytics = (isLast: boolean) => {
  if (isLast) {
    tagSTCAltLastImage();
  } else {
    tagSTCAltImage();
  }
};

const handleCarouselChange = (index: number) => {
  updateCarousel(getFullIndex(index));
  pauseVideo();
  trackAltImageAnalytics(index === filteredImages.value.length);
};

const handleCarouselNext = (page: number) => {
  const isLast = page >= Math.ceil(filteredImages.value.length / 3);
  trackAltImageAnalytics(isLast);
};

watch(isOverlayOpen, (isOpen) => {
  if (isOpen) {
    pauseVideo();
  }
});

const aiImageOverride = computed(() => {
  if (!isAiGeneratedMetaData) return;
  if (isMobile.value) {
    if (isSquareAspectRatio.value) {
      return isBcom ? AI_WATERMARK_CONFIG.collectionGalleryMobile : AI_WATERMARK_CONFIG.collectionGalleryMobileMcom;
    }
    return AI_WATERMARK_CONFIG.collectionGalleryMobileCut;
  }
  if (isSquareAspectRatio.value) {
    return isBcom ? AI_WATERMARK_CONFIG.collectionGalleryDesktop : AI_WATERMARK_CONFIG.collectionGalleryDesktopMcom;
  }
  return AI_WATERMARK_CONFIG.collectionGalleryDesktopCut;
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.image-gallery,
.product-image-wrapper {
  position: relative;
  overflow: hidden;
}

.product-image-wrapper {
  cursor: pointer;
  opacity: 1;
  transition: opacity $stc-overlay-transition-duration $stc-overlay-transition-timing;

  @include breakpoint(medium up) {
    &:hover {
      opacity: 0.65;
    }
  }

  :deep(img) {
    object-fit: cover;
    object-position: center;
  }
}

.product-video-wrapper,
.product-image-wrapper {
  width: 100%;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  flex-shrink: 0;

  @include stc-media-stretch;
}

.is-square-aspect-ratio {
  .product-video-wrapper,
  .product-image-wrapper {
    aspect-ratio: 1 / 1;
  }
}

// Fixed minor CLS: atomic-ui/Carousel does not apply negative margins to the container during SSR,
// only on the client. We manually apply -4px (half of gutter 8px) to match the hydrated state.
.image-gallery {
  :deep(.v-carousel-container) {
    margin-left: -4px !important;
    margin-right: -4px !important;
  }
}
</style>
